import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Download, X } from 'lucide-react';
import { submitLead } from '../lib/leads';
import { trackGenerateLead } from '../lib/tracking';

const BROCHURE_URL = '/Brochure-Punt-Uit.pdf';

function startDownload() {
  const a = document.createElement('a');
  a.href = BROCHURE_URL;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function BrochureLeadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    const ok = await submitLead(
      {
        Naam: name,
        'E-mailadres': email,
        Organisatie: organization || '-',
      },
      { subject: `Brochure gedownload door ${name}` }
    );
    if (ok) {
      trackGenerateLead({ formName: 'brochure', email });
      setStatus('sent');
      startDownload();
    } else {
      setStatus('error');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="brochure-modal-title"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600"
          aria-label="Sluiten"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>

        {status === 'sent' ? (
          <div role="status">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-semibold text-gray-900 mb-1" id="brochure-modal-title">De download is gestart</p>
                <p className="text-gray-600">
                  Bedankt, {name}! Start de download niet vanzelf?{' '}
                  <a href={BROCHURE_URL} download className="underline text-[#0284c7]">
                    Klik dan hier
                  </a>
                  .
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="btn-primary w-full">
              Sluiten
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" id="brochure-modal-title">
              Download de brochure
            </h2>
            <p className="text-gray-600 mb-6">
              Laat je gegevens achter en de download start direct.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="brochure-name" className="block text-sm font-medium mb-1 text-gray-700">
                  Jouw naam
                </label>
                <input
                  id="brochure-name"
                  required
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  placeholder="Jouw naam"
                />
              </div>
              <div>
                <label htmlFor="brochure-email" className="block text-sm font-medium mb-1 text-gray-700">
                  Jouw zakelijke e-mailadres
                </label>
                <input
                  id="brochure-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  placeholder="Jouw e-mailadres"
                />
              </div>
              <div>
                <label htmlFor="brochure-organization" className="block text-sm font-medium mb-1 text-gray-700">
                  Organisatie (optioneel)
                </label>
                <input
                  id="brochure-organization"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  placeholder="Naam van je organisatie"
                />
              </div>
              <button
                type="submit"
                disabled={status === 'sending'}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Download className="w-4 h-4" aria-hidden="true" />
                {status === 'sending' ? 'Even geduld…' : 'Download de brochure'}
              </button>
              {status === 'error' && (
                <p className="text-sm text-red-600" role="alert">
                  Het versturen is helaas niet gelukt. Probeer het opnieuw, of mail ons op{' '}
                  <a href="mailto:info@puntuit.nl" className="underline">info@puntuit.nl</a>.
                </p>
              )}
              <p className="text-xs text-gray-500">
                Je gegevens worden vertrouwelijk behandeld. Zie onze{' '}
                <Link to="/privacyverklaring" className="underline underline-offset-2" onClick={onClose}>privacyverklaring</Link>.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
